import { FormJourneyItem, FormJourneyItemContactInfoAdditionalSettings, LeadForm } from "@plura/shared/types";
import { AnimatePresence, motion } from "framer-motion";
import { useAutoFocusAfterAnimation } from "@plura/client/hooks";
import { useRef } from "react";
import { cn } from "@plura/shared/utils";
import Input from "../../../../molecules/Input";
import PhoneNumberInput from "../../../../molecules/PhoneNumberInput";
import "./contact-info-screen.css";
import { useFormContext } from "react-hook-form";
import { dynamicQuestionContent, getCurrentResponseScore } from "@plura/shared/lead-forms";
import { Checkbox } from "../../../../molecules";

interface Props {
  id: string;
  step: FormJourneyItem
  leadForm: LeadForm;
  editorMode: boolean;
  previewMode: boolean;
  active: boolean;
  onContentClick?: (fieldName: string, blockId: string) => void;
  requiresGDPRConsent?: string;
  hiddenFields?: {
    field: string;
    value: string;
  }[];
}

type ContactField = keyof FormJourneyItemContactInfoAdditionalSettings;

const FIELD_ORDER: ContactField[] = ['first_name', 'last_name', 'phone', 'email', 'company'];

const ContactInfoScreen = ({ id, step, editorMode, leadForm, active, onContentClick, requiresGDPRConsent, hiddenFields, previewMode }: Props) => {
  const additionalSettings = step.additionalSettings as FormJourneyItemContactInfoAdditionalSettings
  const firstNameInputRef = useRef<HTMLInputElement>(null);
  const lastNameInputRef = useRef<HTMLInputElement>(null);
  const phoneInputRef = useRef<HTMLInputElement>(null);
  const emailInputRef = useRef<HTMLInputElement>(null);
  const companyInputRef = useRef<HTMLInputElement>(null);

  const refMap: Record<ContactField, React.RefObject<HTMLInputElement | null>> = {
    first_name: firstNameInputRef,
    last_name: lastNameInputRef,
    phone: phoneInputRef,
    email: emailInputRef,
    company: companyInputRef,
  };

  const form = useFormContext();
  const { conversationId, ...userResponses } = form.getValues()
  const currentResponseScore = userResponses
    ? getCurrentResponseScore(userResponses, leadForm)
    : 0

  const handleContentClick = (field: 'content' | 'secondaryContent') => {
    if ((!editorMode || (editorMode && previewMode)) || !onContentClick) return
    onContentClick(field, id)
  }

  const visibleFields = FIELD_ORDER.filter(field => additionalSettings?.[field]?.visible);

  // Enter moves to the next visible field; on the last one it bubbles up to
  // the global handler and submits the step — no clicking needed.
  const handleFieldKeyDown = (field: ContactField) => (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter' || e.shiftKey) return;
    const next = visibleFields[visibleFields.indexOf(field) + 1];
    if (!next) return;
    e.preventDefault();
    e.stopPropagation();
    refMap[next]?.current?.focus();
  };

  const shouldFocus = (!editorMode || (editorMode && previewMode)) && active;
  const { wrapperRef } = useAutoFocusAfterAnimation(() => {
    const first = visibleFields[0];
    return first ? refMap[first]?.current : null;
  }, shouldFocus);

  const hasAnyValue = !!(firstNameInputRef.current?.value.length || lastNameInputRef.current?.value.length || phoneInputRef.current?.value.length || emailInputRef.current?.value.length || companyInputRef.current?.value.length);

  return (
    <>
      <div className={cn(
        'questionInner',
        step.contentAlignment === 'LEFT' && 'columnLayout',
        step.contentAlignment === 'RIGHT' && 'columnLayout'
      )}>
        <div>
          <h4
            onClick={() => handleContentClick('content')}
            className={
              cn(
                hasAnyValue && 'semiFade',
                'question',
              )}
          >
            <motion.span dangerouslySetInnerHTML={{
              __html: dynamicQuestionContent({
                content: step.content,
                score: currentResponseScore,
                editMode: editorMode,
                previewMode,
                hiddenFields,
                journeyItems: leadForm.journeyItems ?? [],
                userResponses
              })
            }} />
          </h4>
          {
            step.secondaryContent &&
            <p
              onClick={() => handleContentClick('secondaryContent')}
              className={
                cn(
                  hasAnyValue && 'semiFade',
                  'caption',
                )}
            >
              <motion.span
                dangerouslySetInnerHTML={{
                  __html: dynamicQuestionContent({
                    content: step.secondaryContent,
                    score: currentResponseScore,
                    editMode: editorMode,
                    previewMode,
                    hiddenFields,
                    journeyItems: leadForm.journeyItems ?? [],
                    userResponses
                  })
                }}
              />
            </p>
          }
        </div>
        <div>
          <motion.div ref={wrapperRef} className={cn('answerInputWrapper')}>
            <AnimatePresence initial={false}>
              <div className={'formWrapper'}>
                {
                  !!additionalSettings?.first_name?.visible &&
                  <motion.div className={'fieldWrapper'}>
                    <Input
                      ref={firstNameInputRef}
                      id={`${step.blockId}_first_name`}
                      placeholder={`First Name${additionalSettings?.first_name?.required ? '*' : ''}`}
                      className={cn('answerInput', !firstNameInputRef.current?.value.length && 'empty')}
                      autoComplete="given-name"
                      onKeyDown={handleFieldKeyDown('first_name')}
                      data-gramm="false"
                      data-gramm_editor="false"
                      data-enable-grammarly="false"
                    />
                  </motion.div>
                }
                {
                  !!additionalSettings?.last_name?.visible &&
                  <motion.div className={'fieldWrapper'}>
                    <Input
                      ref={lastNameInputRef}
                      id={`${step.blockId}_last_name`}
                      placeholder={`Last Name${additionalSettings?.last_name?.required ? '*' : ''}`}
                      className={cn('answerInput', !lastNameInputRef.current?.value.length && 'empty')}
                      autoComplete="family-name"
                      onKeyDown={handleFieldKeyDown('last_name')}
                      data-gramm="false"
                      data-gramm_editor="false"
                      data-enable-grammarly="false"
                    />
                  </motion.div>
                }
                {
                  !!additionalSettings?.phone?.visible &&
                  <motion.div className={'fieldWrapper'}>
                    <PhoneNumberInput
                      ref={phoneInputRef}
                      id={`${step.blockId}_phone`}
                      placeholder={`(201) 555-0123${additionalSettings?.phone?.required ? '*' : ''}`}
                      elementType={leadForm.elementType}
                      className={cn('answerInput', !phoneInputRef.current?.value.length && 'empty')}
                      onKeyDown={handleFieldKeyDown('phone')}
                      data-gramm="false"
                      data-gramm_editor="false"
                      data-enable-grammarly="false"
                    />
                  </motion.div>
                }
                {
                  !!additionalSettings?.email?.visible &&
                  <motion.div className={'fieldWrapper'}>
                    <Input
                      ref={emailInputRef}
                      id={`${step.blockId}_email`}
                      placeholder={`name@example.com${additionalSettings?.email?.required ? '*' : ''}`}
                      className={cn('answerInput', !emailInputRef.current?.value.length && 'empty')}
                      type="email"
                      inputMode="email"
                      autoComplete="email"
                      autoCapitalize="none"
                      autoCorrect="off"
                      spellCheck={false}
                      onKeyDown={handleFieldKeyDown('email')}
                      data-gramm="false"
                      data-gramm_editor="false"
                      data-enable-grammarly="false"
                    />
                  </motion.div>
                }
                {
                  !!additionalSettings?.company?.visible &&
                  <div className={'fieldWrapperRow'}>
                    <motion.div className={'fieldWrapper'}>
                      <Input
                        ref={companyInputRef}
                        id={`${step.blockId}_company`}
                        placeholder={`Company${additionalSettings?.company?.required ? '*' : ''}`}
                        className={cn('answerInput', !companyInputRef.current?.value.length && 'empty')}
                        autoComplete="organization"
                        onKeyDown={handleFieldKeyDown('company')}
                        data-gramm="false"
                        data-gramm_editor="false"
                        data-enable-grammarly="false"
                      />
                    </motion.div>
                  </div>
                }
              </div>
              {
                requiresGDPRConsent === id && (
                  <div className={'consentCheckbox'}>
                    <Checkbox
                      options={[
                        {
                          value: id,
                          label: leadForm.consentMessageTitle ?? 'I consent to the collection of my personal data'
                        },
                      ]}
                      value={form.getValues('consent')}
                      id={'consent'}
                      onChange={(e) => form.setValue('consent', e.target.checked)}
                      handleOnChange={() => form.clearErrors('consent')}
                      className="answerInput"
                      data-gramm="false"
                      data-gramm_editor="false"
                      data-enable-grammarly="false"
                    />
                  </div>
                )
              }
            </AnimatePresence>
          </motion.div>
        </div>
      </div >
    </>
  );
};


export default ContactInfoScreen
